import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SETUP_STEPS = [
  { id: 'profile', icon: 'fa-school', label: 'School Profile', labelHi: 'स्कूल प्रोफ़ाइल', desc: 'Name, Address, Board, Logo', descHi: 'नाम, पता, बोर्ड, लोगो', route: '/settings', checkKey: 'profile_done' },
  { id: 'classes', icon: 'fa-chalkboard', label: 'Classes & Sections', labelHi: 'कक्षाएं और सेक्शन', desc: 'Create classes and sections', descHi: 'कक्षाएं और section बनाएं', route: '/classes', checkKey: 'classes_done' },
  { id: 'fee_structure', icon: 'fa-indian-rupee-sign', label: 'Fee Structure', labelHi: 'शुल्क संरचना', desc: 'Set annual/monthly fees per class', descHi: 'हर class के लिए fees set करें', route: '/fee-structure', checkKey: 'fees_done' },
  { id: 'students', icon: 'fa-user-graduate', label: 'Students', labelHi: 'छात्र जोड़ें', desc: 'Bulk import CSV or add manually', descHi: 'CSV import या manual add करें', route: '/students', checkKey: 'students_done' },
  { id: 'teachers', icon: 'fa-chalkboard-teacher', label: 'Teachers & Staff', labelHi: 'शिक्षक और स्टाफ', desc: 'Add teaching and non-teaching staff', descHi: 'Teachers और staff add करें', route: '/teachers', checkKey: 'teachers_done' },
  { id: 'whatsapp', icon: 'fa-brands fa-whatsapp', label: 'WhatsApp Connect', labelHi: 'WhatsApp जोड़ें', desc: 'Connect WhatsApp Business API', descHi: 'WhatsApp API connect करें', route: '/settings', checkKey: 'whatsapp_done' },
  { id: 'gallery', icon: 'fa-images', label: 'Gallery', labelHi: 'गैलरी', desc: 'Upload school photos & videos', descHi: 'School photos और videos upload करें', route: '/settings', checkKey: 'gallery_done' },
  { id: 'admission', icon: 'fa-rocket', label: 'Admission Engine', labelHi: 'Admission Engine', desc: 'Setup seats, exams, welcome video', descHi: 'Seats, Exams, Welcome Video setup', route: '/admission-engine', checkKey: 'admission_done' },
];

export default function SetupGuidePage({ token, user }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);

  const schoolId = user?.school_id || '';

  useEffect(() => { if (token && schoolId) checkProgress(); else setLoading(false); }, [token]);

  const checkProgress = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, classesRes, studentsRes, teachersRes] = await Promise.all([
        fetch(`${API_URL}/api/schools/${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/classes/?school_id=${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/students/?school_id=${schoolId}&limit=1`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/teachers/?school_id=${schoolId}&limit=1`, { headers }).then(r => r.json()).catch(() => ({})),
      ]);

      const p = {
        profile_done: !!(schoolRes?.name && schoolRes?.address),
        classes_done: (classesRes?.classes?.length || classesRes?.total || 0) > 0,
        fees_done: false,
        students_done: (studentsRes?.students?.length || studentsRes?.total || 0) > 0,
        teachers_done: (teachersRes?.teachers?.length || teachersRes?.total || 0) > 0,
        whatsapp_done: false,
        gallery_done: false,
        admission_done: false,
      };

      // Check fee structure
      try {
        const feeRes = await fetch(`${API_URL}/api/fee-structure/${schoolId}`, { headers }).then(r => r.json());
        p.fees_done = feeRes?.has_structure || false;
      } catch (e) {}

      setProgress(p);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const completedCount = Object.values(progress).filter(Boolean).length;
  const totalSteps = SETUP_STEPS.length;
  const percentage = Math.round((completedCount / totalSteps) * 100);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="setup-guide-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white">
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {language === 'hi' ? 'Quick Setup Guide — स्कूल सेटअप' : 'Quick Setup Guide'}
        </h1>
        <p className="text-orange-100 text-sm mt-1">
          {language === 'hi' ? '8 आसान steps में अपना school पूरा setup करें' : 'Complete your school setup in 8 easy steps'}
        </p>
        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span>{completedCount}/{totalSteps} {language === 'hi' ? 'पूरे हुए' : 'completed'}</span>
            <span className="font-bold">{percentage}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <div className="bg-white rounded-full h-3 transition-all duration-500" style={{ width: `${percentage}%` }} />
          </div>
        </div>
      </div>

      {/* Prominent UDISE+ Import option */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-5 text-white shadow-lg" data-testid="udise-import-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 text-lg">⚡</span>
              <h2 className="text-lg font-bold">{language === 'hi' ? 'UDISE+ से तुरंत Import करें' : 'Instant Import from UDISE+'}</h2>
              <span className="text-[10px] font-bold px-2 py-0.5 bg-white text-indigo-700 rounded-full">{language === 'hi' ? 'सबसे आसान' : 'EASIEST'}</span>
            </div>
            <p className="text-indigo-100 text-sm">
              {language === 'hi'
                ? 'अपना 11-अंकों का UDISE+ code डालें — School की पूरी सरकारी profile (नाम, principal, students, district आदि) अपने-आप भर जाएगी। कुछ भी manually भरने की ज़रूरत नहीं।'
                : 'Enter your 11-digit UDISE+ code — your full government school profile (name, principal, students, district, etc.) auto-fills. No manual entry needed.'}
            </p>
          </div>
          <button
            onClick={() => navigate('/activate')}
            className="px-5 py-3 bg-white text-indigo-700 rounded-xl font-bold text-sm hover:bg-indigo-50 transition whitespace-nowrap shadow"
            data-testid="udise-import-btn"
          >
            ⚡ {language === 'hi' ? 'UDISE+ Code से Import करें' : 'Import via UDISE+ Code'}
          </button>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SETUP_STEPS.map((step, i) => {
          const done = progress[step.checkKey];
          const isActive = activeStep === step.id;
          return (
            <div
              key={step.id}
              className={`bg-white rounded-xl border-2 p-4 sm:p-5 transition-all cursor-pointer group ${done ? 'border-green-200 bg-green-50/50' : isActive ? 'border-orange-400 shadow-lg' : 'border-gray-100 hover:border-orange-200 hover:shadow-md'}`}
              onClick={() => setActiveStep(isActive ? null : step.id)}
              data-testid={`setup-step-${step.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Step Number / Check */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold ${done ? 'bg-green-500 text-white' : 'bg-orange-100 text-orange-600'}`}>
                  {done ? <i className="fa-solid fa-check"></i> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`fa-solid ${step.icon} ${done ? 'text-green-500' : 'text-gray-400'}`}></i>
                    <h3 className={`font-semibold text-sm ${done ? 'text-green-700' : 'text-gray-800'}`}>
                      {language === 'hi' ? step.labelHi : step.label}
                    </h3>
                    {done && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-600 rounded-full">{language === 'hi' ? 'पूरा' : 'Done'}</span>}
                  </div>
                  <p className="text-xs text-gray-400">{language === 'hi' ? step.descHi : step.desc}</p>

                  {/* Expanded Guide */}
                  {isActive && (
                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-100" data-testid={`guide-${step.id}`}>
                      <StepGuide step={step} language={language} />
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(step.route); }}
                        className="mt-3 w-full py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition"
                        data-testid={`goto-${step.id}`}
                      >
                        <i className="fa-solid fa-arrow-right mr-1"></i>
                        {done ? (language === 'hi' ? 'देखें / Edit करें' : 'View / Edit') : (language === 'hi' ? 'अभी करें' : 'Do Now')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {percentage === 100 && (
        <div className="bg-green-50 rounded-2xl p-6 border border-green-200 text-center" data-testid="setup-complete">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="text-xl font-bold text-green-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {language === 'hi' ? 'बधाई हो! Setup पूरा हो गया!' : 'Congratulations! Setup Complete!'}
          </h2>
          <p className="text-green-600 text-sm mt-1">
            {language === 'hi' ? 'अब आपका school पूरी तरह ready है।' : 'Your school is now fully set up.'}
          </p>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-slate-50 rounded-xl p-4 border text-center">
        <p className="text-sm text-gray-500">
          {language === 'hi' ? 'मदद चाहिए? Aayush AI chatbot से पूछें या WhatsApp पर संपर्क करें' : 'Need help? Ask Aayush AI chatbot or contact us on WhatsApp'}
        </p>
      </div>
    </div>
  );
}

function StepGuide({ step, language }) {
  const guides = {
    profile: {
      hi: ['Settings → Profile tab में जाएं', 'School का नाम, पता, Board, Medium भरें', 'Logo upload करें', 'Save करें'],
      en: ['Go to Settings → Profile tab', 'Fill school name, address, board, medium', 'Upload logo', 'Click Save']
    },
    classes: {
      hi: ['Classes page पर जाएं', '"+ Add Class" click करें', 'Class name और sections भरें (A, B, C)', 'सभी classes add करें (Nursery-12)'],
      en: ['Go to Classes page', 'Click "+ Add Class"', 'Enter class name and sections (A, B, C)', 'Add all classes (Nursery-12)']
    },
    fee_structure: {
      hi: ['Fee Structure page पर जाएं', 'हर class के लिए fees add करें', 'Tuition, Admission, Transport, Activity fees अलग-अलग', 'Save करें — Admission Portal में auto दिखेगा'],
      en: ['Go to Fee Structure page', 'Add fees for each class', 'Separate: Tuition, Admission, Transport, Activity', 'Save — auto-shows in Admission Portal']
    },
    students: {
      hi: ['Students page पर जाएं', 'Option 1: "Bulk Import" → CSV file upload करें', 'Option 2: "+ Add Student" → Manual add करें', 'Name, Class, Guardian details भरें'],
      en: ['Go to Students page', 'Option 1: "Bulk Import" → Upload CSV', 'Option 2: "+ Add Student" → Add manually', 'Fill name, class, guardian details']
    },
    teachers: {
      hi: ['Teachers page पर जाएं', '"+ Add Teacher" click करें', 'Name, Phone, Subject, Qualification भरें', 'Salary info add करें (HR Payroll के लिए)'],
      en: ['Go to Teachers page', 'Click "+ Add Teacher"', 'Fill name, phone, subject, qualification', 'Add salary info (for HR Payroll)']
    },
    whatsapp: {
      hi: ['Settings → Integrations tab पर जाएं', 'WhatsApp Business API key paste करें', '"Test Connection" click करें', 'Connected होने पर auto-messages चालू हो जाएंगे'],
      en: ['Go to Settings → Integrations tab', 'Paste WhatsApp Business API key', 'Click "Test Connection"', 'Auto-messages will start after connection']
    },
    gallery: {
      hi: ['Settings → Gallery tab पर जाएं', 'School की photos upload करें (Building, Events, Sports)', 'Videos add करें (YouTube links)', 'Featured photos select करें — ये public page पर दिखेंगे'],
      en: ['Go to Settings → Gallery tab', 'Upload school photos (Building, Events, Sports)', 'Add videos (YouTube links)', 'Select featured photos — shown on public page']
    },
    admission: {
      hi: ['Admission Engine page पर जाएं', 'Step 1: Seat Counter setup — हर class में कितनी seats', 'Step 2: AI Exam create — Class-wise entrance exam', 'Step 3: Welcome Video template setup'],
      en: ['Go to Admission Engine page', 'Step 1: Seat Counter — seats per class', 'Step 2: Create AI Entrance Exam per class', 'Step 3: Setup Welcome Video template']
    },
  };

  const g = guides[step.id] || { hi: ['Setup करें'], en: ['Complete setup'] };
  const steps = language === 'hi' ? g.hi : g.en;
  const API = process.env.REACT_APP_BACKEND_URL;

  return (
    <>
      <ol className="space-y-1.5">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-orange-800">
            <span className="w-5 h-5 bg-orange-200 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold text-orange-700">{i + 1}</span>
            {s}
          </li>
        ))}
      </ol>
      {step.id === 'students' && (
        <div className="mt-3 pt-3 border-t border-orange-200" data-testid="students-sample-downloads">
          <p className="text-[11px] font-bold text-orange-900 mb-2">
            {language === 'hi' ? '📥 Bulk Import के लिए Sample CSV files:' : '📥 Sample CSV files for Bulk Import:'}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={`${API}/api/bulk/template/basic/download`}
              download="student_import_basic_template.csv"
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[11px] font-semibold hover:bg-blue-700 transition flex items-center gap-1.5"
              data-testid="setup-download-basic-template"
            >
              <i className="fa-solid fa-download"></i>
              {language === 'hi' ? 'Basic Sample (17 fields)' : 'Basic Sample (17 fields)'}
            </a>
            <a
              href={`${API}/api/bulk/template/full/download`}
              download="student_import_full_template.csv"
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-semibold hover:bg-emerald-700 transition flex items-center gap-1.5"
              data-testid="setup-download-full-template"
            >
              <i className="fa-solid fa-download"></i>
              {language === 'hi' ? 'पूरा Govt Sample (55 fields)' : 'Full Govt Sample (55 fields)'}
            </a>
          </div>
          <p className="text-[10px] text-orange-600 mt-1.5">
            {language === 'hi'
              ? 'Basic = ज़रूरी 17 fields · Full = student की सभी सरकारी detail (55 fields)'
              : 'Basic = essential 17 fields · Full = all government student details (55 fields)'}
          </p>
        </div>
      )}
    </>
  );
}
